import React from "react";
import { Avatar, Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import { LockOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import history from "utils/history";
import { useAppSelector } from "redux/hooks";
import {
  selectUserName,
  selectEmail,
  selectProfileImage,
} from "redux/authSlice";
import { getNameInitials } from "helpers/methods";
import { useFeatures } from "hooks/use-feature";
import strings from "global/constants/StringConstants";
import settingsShellTokens, {
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";
import useSettingsNavSections, {
  SettingsNavItem,
} from "screens/Settings/components/SettingsNavSections";

const { colors, nav, userCard } = settingsShellTokens;
const { tablet } = settingsShellResponsiveTokens;

interface SettingsSideMenuProps {
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

const SettingsSideMenu: React.FC<SettingsSideMenuProps> = ({
  activeTab,
  onTabChange,
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const userName = useAppSelector(selectUserName);
  const userEmail = useAppSelector(selectEmail);
  const profileImage = useAppSelector(selectProfileImage);
  const visibleMenuSections = useSettingsNavSections();
  const { state: featureState } = useFeatures();

  const getImageUrl = () => {
    if (!profileImage || profileImage === "data:image/png;base64,") {
      return "";
    }
    return profileImage;
  };

  const getIsActive = (name: string, path: string) => {
    if (activeTab !== undefined) {
      return activeTab === name;
    }
    if (name === "My Profile") {
      return currentPath === path;
    }
    return currentPath === path || currentPath.startsWith(path);
  };

  const handleItemClick = (name: string, path: string) => {
    if (getIsActive(name, path)) {
      return;
    }
    if (onTabChange) {
      onTabChange(name);
    } else {
      history.push(path);
    }
  };

  return (
    <Box
      component="nav"
      aria-label="Settings"
      sx={{
        display: "flex",
        flexDirection: "column",
        // The tablet reference runs the column full height and drops the user
        // card to its foot; on desktop the card simply follows the last group.
        flex: { sm: 1, md: "none" },
        minHeight: 0,
      }}
    >
      {visibleMenuSections.map((section, secIdx) => (
        <React.Fragment key={section.title}>
          <Typography
            component="div"
            sx={{
              textTransform: "uppercase",
              fontSize: {
                sm: tablet.nav.sectionLabelFontSize,
                md: nav.sectionLabelFontSize,
              },
              fontWeight: nav.sectionLabelFontWeight,
              letterSpacing: {
                sm: tablet.nav.sectionLabelLetterSpacing,
                md: nav.sectionLabelLetterSpacing,
              },
              lineHeight: "normal",
              color: colors.sectionLabel,
              p: {
                sm:
                  secIdx === 0
                    ? tablet.nav.sectionLabelPadding
                    : tablet.nav.sectionLabelPaddingTop,
                md:
                  secIdx === 0
                    ? nav.sectionLabelPadding
                    : nav.sectionLabelPaddingTop,
              },
            }}
          >
            {section.title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { sm: tablet.nav.itemMarginBottom, md: nav.itemGap },
            }}
          >
            {section.items.map((item: SettingsNavItem) => {
              const isActive = getIsActive(item.name, item.path);
              const gate = item.featureKey
                ? featureState(item.featureKey)
                : undefined;
              const isGated =
                !!gate && gate.status !== strings.FEATURE_STATUS_AVAILABLE;
              const gateTooltip = isGated
                ? gate!.status === strings.FEATURE_STATUS_NOT_IN_PLAN
                  ? strings.FEATURE_GATE_UPSELL_TOOLTIP
                  : ((gate as any).disabledMessage ??
                    strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
                : "";

              const row = (
                <ButtonBase
                  key={item.name}
                  disableRipple
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => handleItemClick(item.name, item.path)}
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: { sm: tablet.nav.itemGap, md: nav.itemIconGap },
                    height: { sm: "auto", md: nav.itemHeight },
                    p: { sm: tablet.nav.itemPadding, md: 0 },
                    px: { md: nav.itemPaddingX },
                    borderRadius: {
                      sm: tablet.nav.itemRadius,
                      md: nav.itemRadius,
                    },
                    // ButtonBase renders a <button>, which would otherwise fall
                    // back to the browser's UI font instead of Montserrat.
                    fontFamily: "inherit",
                    fontSize: { sm: tablet.nav.itemFontSize, md: nav.itemFontSize },
                    fontWeight: {
                      sm: isActive
                        ? tablet.nav.itemFontWeightActive
                        : tablet.nav.itemFontWeight,
                      md: nav.itemFontWeight,
                    },
                    textAlign: "left",
                    bgcolor: {
                      sm: isActive ? tablet.nav.itemBgActive : "transparent",
                      md: isActive ? colors.itemBgActive : "transparent",
                    },
                    color: isGated
                      ? colors.sectionLabel
                      : {
                          sm: isActive
                            ? tablet.nav.itemColorActive
                            : tablet.nav.itemColor,
                          md: isActive ? colors.itemTextActive : colors.itemText,
                        },
                    "&:hover": {
                      bgcolor: {
                        sm: isActive ? tablet.nav.itemBgActive : colors.itemBgHover,
                        md: isActive ? colors.itemBgActive : colors.itemBgHover,
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      width: { sm: tablet.nav.itemIconSize, md: nav.itemIconSize },
                      height: { sm: tablet.nav.itemIconSize, md: nav.itemIconSize },
                    },
                    "&.Mui-focusVisible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  {item.icon}
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.name}
                  </Box>
                  {isGated &&
                    (gate!.status === strings.FEATURE_STATUS_ADMIN_DISABLED ? (
                      <WarningAmberOutlined
                        sx={{ fontSize: 14, opacity: 0.65, flexShrink: 0 }}
                      />
                    ) : (
                      <LockOutlined
                        sx={{ fontSize: 14, opacity: 0.65, flexShrink: 0 }}
                      />
                    ))}
                </ButtonBase>
              );

              if (!isGated) return row;

              return (
                <Tooltip
                  key={item.name}
                  title={gateTooltip}
                  placement="right"
                  arrow
                  describeChild
                >
                  <Box component="span" sx={{ display: "block" }}>
                    {row}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </React.Fragment>
      ))}

      {/* User card pinned under the nav groups */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { sm: tablet.userCard.gap, md: userCard.gap },
          mt: { sm: "auto", md: userCard.marginTop },
          p: { sm: tablet.userCard.padding, md: userCard.padding },
          borderRadius: { sm: tablet.userCard.radius, md: userCard.radius },
          bgcolor: colors.userCardBg,
        }}
      >
        <Avatar
          src={getImageUrl()}
          alt={userName || "User"}
          sx={{
            width: { sm: tablet.userCard.avatarSize, md: userCard.avatarSize },
            height: { sm: tablet.userCard.avatarSize, md: userCard.avatarSize },
            flexShrink: 0,
            bgcolor: colors.avatarBg,
            color: colors.avatarText,
            fontSize: {
              sm: tablet.userCard.avatarFontSize,
              md: userCard.avatarFontSize,
            },
            fontWeight: userCard.avatarFontWeight,
          }}
        >
          {getNameInitials(userName)}
        </Avatar>
        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: userCard.textGap,
          }}
        >
          <Typography
            component="div"
            noWrap
            sx={{
              fontSize: {
                sm: tablet.userCard.nameFontSize,
                md: userCard.nameFontSize,
              },
              fontWeight: userCard.nameFontWeight,
              color: colors.userName,
              lineHeight: "normal",
            }}
          >
            {userName}
          </Typography>
          <Typography
            component="div"
            noWrap
            sx={{
              fontSize: {
                sm: tablet.userCard.emailFontSize,
                md: userCard.emailFontSize,
              },
              color: colors.userEmail,
              lineHeight: "normal",
            }}
          >
            {userEmail}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsSideMenu;
